import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Wrench, ArrowRight, Loader2 } from "lucide-react";
import { Car3D } from "@/components/Car3D";
import { SERVICE_ICONS } from "@/lib/icons";

const PARTS = {
  engine: { img: "/parts/engine.png", label: "Dzinēja bloks" },
  brake: { img: "/parts/brake.png", label: "Bremžu disks" },
  service: { img: "/parts/service.png", label: "Apkopes komplekts" },
};

const SHOTS = {
  activity: { view: "cabin", part: null, hud: true, zone: "cabin", xray: 0.1, note: "Salons · stūre un spidometrs" },
  cog: { view: "engine", part: "engine", hood: true, zone: "hood", xray: 0.3, note: "Motora telpa atvērta" },
  flame: { view: "sideFull", part: null, noPointer: true, note: "Virsbūve · metināšanas darbi" },
  disc: { view: "wheel", part: "brake", note: "Ritošā daļa · bremžu mehānisms" },
  wrench: { view: "engine", part: "service", hood: true, zone: "hood", xray: 0.35, note: "Apkopes zona zem pārsega" },
  zap: { view: "cabin", part: null, hud: true, zone: "cabin", xray: 0.14, note: "ECU · vadības bloks" },
};
const IDLE = { view: "idle", part: null, note: "" };

const FALLBACK = [
  { id: "s1", title: "Datordiagnostika", icon: "activity", category: "Diagnostika", price_from: 25, price_unit: "EUR", duration: "30–60 min", description: "Pilna elektronikas skenēšana ar dīleru līmeņa iekārtām." },
  { id: "s2", title: "Dzinēja remonts", icon: "cog", category: "Dzinējs", price_from: 180, price_unit: "EUR", duration: "1–5 dienas", description: "Galvas remonts, blīves, sadales ķēde, kapitālremonts." },
  { id: "s3", title: "Ritošās daļas remonts", icon: "disc", category: "Ritošā daļa", price_from: 60, price_unit: "EUR", duration: "60–120 min", description: "Bremžu disku un kluču maiņa, balstiekārtas un riteņu gultņu remonts." },
];

export const HeroStage = ({ services }) => {
  // only services explicitly flagged for the 3D hero (and with a defined camera shot) appear here
  const hero = services.filter((s) => s.in_hero && SHOTS[s.icon]);
  const list = hero.length ? hero : FALLBACK;
  const [active, setActive] = useState(0);
  const [engaged, setEngaged] = useState(false);
  const pausedUntil = useRef(0);
  const shotRef = useRef(IDLE);
  const pointerRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setEngaged(true), 3200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const i = setInterval(() => {
      if (Date.now() < pausedUntil.current) return;
      setActive((a) => (a + 1) % list.length);
    }, 7000);
    return () => clearInterval(i);
  }, [list.length]);

  const current = list[Math.min(active, list.length - 1)] || list[0];
  const [scanning, setScanning] = useState(false);
  const [shown, setShown] = useState(0);

  // scan sweep masks every transition between shots
  useEffect(() => {
    setScanning(true);
    const a = setTimeout(() => setShown(active), 420);
    const b = setTimeout(() => setScanning(false), 1250);
    return () => {
      clearTimeout(a);
      clearTimeout(b);
    };
  }, [active]);

  const shownService = list[Math.min(shown, list.length - 1)] || list[0];
  const shot = useMemo(() => (engaged ? SHOTS[shownService?.icon] || IDLE : IDLE), [shownService, engaged]);
  shotRef.current = shot;

  const pick = (i) => {
    pausedUntil.current = Date.now() + 18000;
    setActive(i);
    setEngaged(true);
  };

  return (
    <div className="grid gap-8 lg:grid-cols-[262px_1fr] lg:gap-y-4 lg:gap-x-10" data-testid="hero-stage">
      {/* clickable service rail */}
      <div className="order-2 min-w-0 lg:order-1 lg:col-start-1 lg:row-start-1">
        <div className="mb-4 flex items-center gap-3 font-mono-t text-[10px] uppercase tracking-[0.25em] text-slate-500">
          <span className="h-px w-6 bg-rose-600" /> Pakalpojumi
        </div>
        <div className="relative">
          <div className="flex w-full max-w-full gap-1.5 lg:flex-col lg:gap-2" data-testid="hero-service-rail">
          {list.map((sv, i) => {
            const Icon = SERVICE_ICONS[sv.icon] || Wrench;
            const part = PARTS[SHOTS[sv.icon]?.part];
            const isActive = i === active;
            return (
              <button
                key={sv.id}
                onClick={() => pick(i)}
                data-testid={`hero-service-${sv.id}`}
                title={sv.title}
                className={`group flex min-w-0 flex-1 flex-col items-center gap-1 border px-1 py-2 text-left transition-all duration-300 lg:flex-none lg:flex-row lg:gap-3 lg:px-3 lg:py-2.5 ${
                  isActive ? "border-rose-600 bg-rose-600/12" : "border-white/10 bg-white/[0.02] hover:border-white/30"
                }`}
              >
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden border lg:h-10 lg:w-10 ${isActive ? "border-rose-500/70 bg-black/60" : "border-white/10 bg-black/40"}`}>
                  {part ? (
                    <img src={part.img} alt={sv.title} className="h-6 w-6 object-contain lg:h-8 lg:w-8" />
                  ) : (
                    <Icon className={`h-4 w-4 ${isActive ? "text-rose-400" : "text-slate-500"}`} strokeWidth={1.6} />
                  )}
                </span>
                <span className="min-w-0 max-w-full">
                  <span className={`hidden truncate font-head text-[15px] font-bold uppercase leading-tight tracking-tight lg:block ${isActive ? "text-white" : "text-slate-300"}`}>
                    {sv.title}
                  </span>
                  <span className={`line-clamp-2 block break-words text-center font-head text-[8px] font-bold uppercase leading-[1.15] tracking-tight lg:hidden ${isActive ? "text-rose-300" : "text-slate-400"}`}>
                    {sv.title}
                  </span>
                  <span className="hidden font-mono-t text-[9px] uppercase tracking-[0.15em] text-slate-500 lg:block">
                    no {sv.price_from} {sv.price_unit || "EUR"}
                  </span>
                </span>
              </button>
            );
          })}
          </div>
        </div>
      </div>

      {/* 3D stage */}
      <div className="order-1 lg:order-2 lg:col-start-2 lg:row-start-1">
        <div className="relative aspect-[3/4] w-full overflow-hidden border border-white/[0.07] bg-[#09090c] sm:aspect-[16/9]">
          <div className="grid-floor absolute inset-0 opacity-30" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_62%,rgba(225,29,72,0.20),transparent_62%)]" />

          <Car3D shotRef={shotRef} pointerRef={pointerRef} />

          {/* 3D loading hint */}
          <motion.div
            className="pointer-events-none absolute bottom-4 left-4 flex items-center gap-2"
            initial={{ opacity: 1 }}
            animate={{ opacity: 0 }}
            transition={{ delay: 3.2, duration: 0.8 }}
          >
            <Loader2 className="h-4 w-4 animate-spin text-rose-500" />
            <span className="font-mono-t text-[9px] uppercase tracking-[0.3em] text-slate-500">Ielādē 3D modeli</span>
          </motion.div>

          {/* diagnostic HUD overlay */}
          <motion.div
            className="pointer-events-none absolute inset-0"
            animate={{ opacity: shot.hud ? 1 : 0 }}
            transition={{ duration: 0.6 }}
            data-testid="hero-hud"
          >
            <div
              className="absolute inset-0"
              style={{
                background:
                  "repeating-linear-gradient(0deg, rgba(225,29,72,0.09) 0px, rgba(225,29,72,0.09) 1px, transparent 1px, transparent 4px)",
              }}
            />
            <motion.div
              className="absolute left-0 h-[2px] w-full bg-rose-500/80"
              style={{ boxShadow: "0 0 18px 4px rgba(225,29,72,0.6)" }}
              animate={{ top: ["8%", "88%"] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            />
            <div className="absolute bottom-4 left-4 font-mono-t text-[9px] uppercase leading-relaxed tracking-[0.2em] text-rose-400/90">
              OBD-II · LIVE DATA
              <br />
              DTC SCAN ····· OK
            </div>
          </motion.div>

          {/* active part shown on the side */}
          <AnimatePresence mode="wait">
            {shot.part && (
              <motion.div
                key={shot.part}
                className="pointer-events-none absolute right-3 top-1/2 w-[30%] max-w-[230px] -translate-y-1/2 border border-white/10 bg-[#0b0b0f]/85 p-3 backdrop-blur-md sm:right-5 sm:p-4"
                initial={{ opacity: 0, x: 60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 40 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                data-testid="hero-side-part"
              >
                <div className="flex items-center gap-2 font-mono-t text-[8px] uppercase tracking-[0.22em] text-rose-400">
                  <span className="h-1 w-1 rounded-full bg-rose-500" /> Detaļa
                </div>
                <motion.img
                  src={PARTS[shot.part].img}
                  alt={PARTS[shot.part].label}
                  data-testid={`hero-part-${shot.part}`}
                  className="mx-auto mt-2 w-[85%] object-contain"
                  style={{ filter: "drop-shadow(0 0 22px rgba(225,29,72,0.55))" }}
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                />
                <div className="mt-2 font-head text-sm font-bold uppercase leading-tight tracking-tight text-white">
                  {PARTS[shot.part].label}
                </div>
                <div className="font-mono-t text-[8px] uppercase tracking-[0.2em] text-slate-500">
                  Oriģinālā / OEM
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* scan sweep transition between shots */}
          <AnimatePresence>
            {scanning && (
              <motion.div
                className="pointer-events-none absolute inset-0 overflow-hidden"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                data-testid="hero-scan-transition"
              >
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "repeating-linear-gradient(0deg, rgba(225,29,72,0.10) 0px, rgba(225,29,72,0.10) 1px, transparent 1px, transparent 5px)",
                  }}
                />
                <motion.div
                  className="absolute inset-y-0 w-[3px] bg-rose-500"
                  style={{ boxShadow: "0 0 40px 12px rgba(225,29,72,0.55)" }}
                  initial={{ left: "-4%" }}
                  animate={{ left: "104%" }}
                  transition={{ duration: 1.15, ease: "easeInOut" }}
                />
                <div className="absolute left-4 top-4 font-mono-t text-[9px] uppercase tracking-[0.25em] text-rose-400/90">
                  SCAN ····· {shownService?.category || "SERVISS"}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* on-car annotation — glued to the real 3D part via screen projection */}
          <div
            ref={pointerRef}
            className="pointer-events-none absolute left-0 top-0 h-0 w-0"
            style={{ transform: "translate3d(-9999px,-9999px,0)" }}
            data-testid="hero-annotation"
          >
            <motion.div
              className="relative h-0 w-0"
              animate={{ opacity: engaged && !shot.noPointer ? 1 : 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              {/* pulsing dot exactly on the part */}
              <span className="absolute left-0 top-0 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rotate-45 border border-rose-500 bg-rose-600/50" />
              <motion.span
                className="absolute left-0 top-0 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full border border-rose-500/70"
                animate={{ scale: [0.5, 1.5], opacity: [0.8, 0] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
              />
              {/* leader line up to the label */}
              <div
                className="absolute left-0 -top-[64px] h-[64px] w-px -translate-x-1/2 bg-gradient-to-t from-rose-500/90 to-rose-500/20 sm:-top-[104px] sm:h-[104px]"
              />
              <div
                data-label
                className="absolute bottom-[64px] left-0 sm:bottom-[104px]"
                style={{ transform: "translateX(-50%)" }}
              >
                <motion.div
                  key={shownService?.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="whitespace-nowrap border border-rose-500/50 bg-[#0b0b0f]/90 px-3 py-2 backdrop-blur-md"
                >
                  <div className="font-mono-t text-[9px] uppercase tracking-[0.22em] text-rose-400">
                    {shot.note || shownService?.category || "Serviss"}
                  </div>
                  <div className="font-head text-base font-bold uppercase tracking-tight text-white">{shownService?.title}</div>
                </motion.div>
              </div>
            </motion.div>
          </div>

          <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_130px_44px_rgba(0,0,0,0.8)]" />
          <div className="pointer-events-none absolute bottom-3 right-4 font-mono-t text-[9px] uppercase tracking-[0.25em] text-slate-600">
            3D · TURBOLUX LAB
          </div>
        </div>
      </div>

      {/* detail card */}
      <div className="order-3 lg:col-start-2 lg:row-start-2">
        <motion.div
          key={current?.id}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          data-testid="hero-part-info"
          className="relative border border-white/10 bg-[#0b0b0f]/90 p-5 backdrop-blur-xl sm:p-6"
        >
          <div className="flex items-center gap-3 font-mono-t text-[10px] uppercase tracking-[0.28em] text-rose-500">
            0{active + 1} / 0{list.length}
            <span className="h-px flex-1 bg-white/10" />
            no {current?.price_from} {current?.price_unit || "EUR"}
          </div>
          <h3 className="mt-3 font-head text-2xl font-extrabold uppercase italic tracking-tight text-white sm:text-3xl">
            {current?.title}
          </h3>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-400">{current?.description}</p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {[current?.category, current?.duration].filter(Boolean).map((chip) => (
              <span key={chip} className="border border-white/10 px-3 py-1.5 font-mono-t text-[9px] uppercase tracking-[0.18em] text-slate-400">
                {chip}
              </span>
            ))}
            <a
              href="#pieteikums"
              data-testid="hero-part-cta"
              className="ml-auto inline-flex items-center gap-2 border border-rose-600/60 bg-rose-600/10 px-4 py-2 font-mono-t text-[10px] uppercase tracking-[0.2em] text-rose-300 transition-colors hover:bg-rose-600 hover:text-white"
            >
              Pieteikt šo darbu <ArrowRight className="h-3 w-3" />
            </a>
          </div>
        </motion.div>

        <div className="mt-3 flex gap-1.5">
          {list.map((sv, i) => (
            <button
              key={sv.id}
              onClick={() => pick(i)}
              data-testid={`hero-bar-${sv.id}`}
              aria-label={sv.title}
              className="h-[3px] flex-1 overflow-hidden bg-white/10"
            >
              <motion.span
                className="block h-full bg-rose-500"
                animate={{ width: i === active ? "100%" : "0%" }}
                transition={{ duration: i === active ? 6.8 : 0.3, ease: "linear" }}
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
